<template>
  <v-container>
    <v-card>
      <v-card-title>
        <span>Order Details</span>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredPieces"
        :expanded.sync="localExpandedItems"
        item-key="Reference"
        show-expand
        dense
        hide-default-footer
      >
        <template #item.Reference="{ item }">
          <td>{{ item.Reference }}</td>
        </template>

        <template #item.deliveredItemsCount="{ item }">
          <td>{{ item.deliveredItems?.length ?? 0 }} item(s)</td>
        </template>

        <template #expanded-item="{ item }">
          <td colspan="4">
            <v-data-table
              :headers="subTableHeaders"
              :items="item.deliveredItems ?? []"
              :items-per-page="-1"
              dense
              hide-default-footer
            >
              <template #item="{ item: subItem }">
                <tr>
                  <td>{{ subItem.patientIds ?? "unknown" }}</td>
                  <td>{{ subItem.descriptions ?? "unknown" }}</td>
                </tr>
              </template>
            </v-data-table>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { PackageDeliveryInfo } from './OrderCard.vue';

const referenceStates: Record<string, boolean> = {};

export function cleanupOrphanedReferenceStates(validReferences: Set<string>) {
  Object.keys(referenceStates).forEach(reference => {
    if (!validReferences.has(reference)) {
      delete referenceStates[reference];
    }
  });
}

// New function to reset all reference states for a specific order
export function resetReferenceStates(orderKey: string) {
  Object.keys(referenceStates).forEach(reference => {
    if (reference.startsWith(orderKey)) {
      referenceStates[reference] = false;
    }
  });
}

export default defineComponent({
  props: {
    pieces: {
      type: Array as PropType<PackageDeliveryInfo | undefined>,
      required: true
    },
    orderKey: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      headers: [
        { text: 'Reference', align: 'start', value: 'Reference' },
        { text: 'Delivered Items Count', value: 'deliveredItemsCount' }
      ],
      subTableHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Description', value: 'description' }
      ]
    };
  },
  computed: {
    filteredPieces() {
      return this.pieces ?? [];
    },
    localExpandedItems: {
      get() {
        return this.filteredPieces?.filter(piece => {
          const referenceKey = `${this.orderKey}-${piece.Reference}`;
          return referenceStates[referenceKey];
        }) || [];
      },
      set(expandedItems: Array<{ Reference: string; deliveredItems?: { patientIds?: string; descriptions?: string; }[] }>) {
        const expandedReferences = new Set(expandedItems.map(item => item.Reference));

        this.filteredPieces?.forEach(piece => {
          const referenceKey = `${this.orderKey}-${piece.Reference}`;
          referenceStates[referenceKey] = expandedReferences.has(piece.Reference);
        });
      }
    }
  }
});
</script>

<style scoped>
.v-data-table td {
  word-wrap: break-word;
}
</style>
